import React, { useState, useEffect } from 'react';
import {
  Search,
  Funnel
} from 'lucide-react';
import { Link } from 'react-router-dom';
import userService from '../../services/userService';
import LoadingSpinner from '../../components/LoadingSpinner';
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from '../../components/ui/select';

const Influencers = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [platformFilter, setPlatformFilter] = useState('all');
  const [followerRangeFilter, setFollowerRangeFilter] = useState('all');
  const [ageFilter, setAgeFilter] = useState('all');
  const [sortBy, setSortBy] = useState('relevance');
  const [influencers, setInfluencers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  useEffect(() => {
    const fetchInfluencers = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Get all influencers
        const filters = {};
        
        // Apply category filter if not 'all'
        if (categoryFilter !== 'all') {
          filters.categories = [categoryFilter];
        }
        
        const influencersData = await userService.getAllInfluencers(filters);
        
        // Ensure uniqueness by ID at the component level
        const uniqueMap = new Map();
        influencersData.forEach(influencer => {
          if (influencer && influencer.id) {
            uniqueMap.set(influencer.id, influencer);
          }
        });
        
        const uniqueInfluencers = Array.from(uniqueMap.values());

        
        // Only update state if we have data and component is still mounted
        setInfluencers(uniqueInfluencers);
      } catch (err) {
        console.error('Error fetching influencers:', err);
        setError('Failed to load influencers. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchInfluencers();
    
    // Cleanup function to prevent state updates after unmount
    return () => {
      // This helps prevent state updates after the component unmounts
    };
  }, [categoryFilter]);
  // Helper function to calculate total followers
  const calculateTotalFollowers = (influencer) => {
    if (!influencer?.influencerDetails?.followers) return 0;
    
    return Object.values(influencer.influencerDetails.followers)
      .reduce((sum, count) => sum + (Number(count) || 0), 0);
  };
  
  // Helper function to determine platforms from social accounts
  const getPlatforms = (influencer) => {
    if (!influencer?.influencerDetails?.socialAccounts) return [];
    
    const platforms = [];
    const accounts = influencer.influencerDetails.socialAccounts;
    
    if (accounts.instagram) platforms.push('Instagram');
    if (accounts.tiktok) platforms.push('TikTok');
    if (accounts.youtube) platforms.push('YouTube');
    if (accounts.facebook) platforms.push('Facebook');
    if (accounts.twitter) platforms.push('Twitter');
    if (accounts.blog) platforms.push('Blog');
    
    return platforms;
  };
  
  // Filter and sort influencers
  
  // Ensure we're working with valid influencers (defensive programming)
  const validInfluencers = influencers.filter(influencer => influencer && influencer.id);
  
  // Always ensure uniqueness at render time (belt and suspenders approach)
  const uniqueInfluencersMap = new Map();
  validInfluencers.forEach(influencer => {
    if (!uniqueInfluencersMap.has(influencer.id)) {
      uniqueInfluencersMap.set(influencer.id, influencer);
    }
  });
  
  const influencersToFilter = Array.from(uniqueInfluencersMap.values());
  
  // Log for debugging
  if (validInfluencers.length !== influencersToFilter.length) {

  }
  
  const filteredInfluencers = influencersToFilter
    .filter(influencer => {
      const name = influencer.displayName || '';
      const username = influencer.email || '';
      const bio = influencer.influencerDetails?.bio || '';
      const searchLower = searchTerm.toLowerCase();
      
      return name.toLowerCase().includes(searchLower) || 
             username.toLowerCase().includes(searchLower) ||
             bio.toLowerCase().includes(searchLower);
    })
    .filter(influencer => {
      if (categoryFilter === 'all') return true;
      return influencer.influencerDetails?.categories?.includes(categoryFilter);
    })
    .filter(influencer => {
      if (platformFilter === 'all') return true;
      const platforms = getPlatforms(influencer);
      return platforms.includes(platformFilter);
    })
    .filter(influencer => {
      const totalFollowers = calculateTotalFollowers(influencer);
      
      if (followerRangeFilter === 'all') return true;
      if (followerRangeFilter === 'under50k') return totalFollowers < 50000;
      if (followerRangeFilter === '50k-100k') return totalFollowers >= 50000 && totalFollowers < 100000;
      if (followerRangeFilter === '100k-250k') return totalFollowers >= 100000 && totalFollowers < 250000;
      if (followerRangeFilter === '250k-500k') return totalFollowers >= 250000 && totalFollowers < 500000;
      if (followerRangeFilter === '500k-1m') return totalFollowers >= 500000 && totalFollowers < 1000000;
      if (followerRangeFilter === 'over1m') return totalFollowers >= 1000000;
      return true;
    })
    .filter(influencer => {
      if (ageFilter === 'all') return true;
      
      const age = parseInt(influencer.age || influencer.influencerDetails?.age || 0);
      if (!age) return false; // Hide influencers without age data when filtering by age
      
      if (ageFilter === '18-24') return age >= 18 && age <= 24;
      if (ageFilter === '25-34') return age >= 25 && age <= 34;
      if (ageFilter === '35-44') return age >= 35 && age <= 44;
      if (ageFilter === '45-54') return age >= 45 && age <= 54;
      if (ageFilter === '55+') return age >= 55;
      return true;
    })
    .sort((a, b) => {
      if (sortBy === 'followers') {
        return calculateTotalFollowers(b) - calculateTotalFollowers(a);
      }
      if (sortBy === 'engagement') {
        const engA = parseFloat(a.influencerDetails?.engagementRate || 0);
        const engB = parseFloat(b.influencerDetails?.engagementRate || 0);
        return engB - engA;
      }
      // Default to sorting by followers if other criteria not available
      return calculateTotalFollowers(b) - calculateTotalFollowers(a);
    });
  
  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center mb-3">
          <h2 className="text-2xl font-semibold text-gray-800">Influencer Directory</h2>
        </div>
        <p className="text-gray-600">Find and connect with influencers for your campaigns</p>
      </div>
      
      {/* Search and Filters */}
      <div className="bg-white rounded-lg shadow-md border border-gray-100 p-6 mb-6">
        <div className="mb-5">
          <div className="flex items-center w-full border border-gray-300 rounded-md focus-within:ring-2 focus-within:ring-primary focus-within:border-primary overflow-hidden">
            <div className="px-4 py-2.5">
              <Search className="h-5 w-5 text-gray-400" aria-hidden="true" />
            </div>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="block w-full py-2.5 border-0 focus:ring-0 sm:text-sm"
              placeholder="Search by name, username, or bio"
            />
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-4 gap-5">
          <div>
            <label htmlFor="categoryFilter" className="block text-sm font-medium text-gray-700 mb-2">Category</label>
            <div className="relative">
              <Select
                value={categoryFilter}
                onValueChange={(value) => setCategoryFilter(value)}
              >
                <SelectTrigger id="categoryFilter" className="w-full">
                  <SelectValue placeholder="All Categories" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  <SelectItem value="Fashion">Fashion</SelectItem>
                  <SelectItem value="Beauty">Beauty</SelectItem>
                  <SelectItem value="Fitness">Fitness</SelectItem>
                  <SelectItem value="Food">Food</SelectItem>
                  <SelectItem value="Travel">Travel</SelectItem>
                  <SelectItem value="Technology">Technology</SelectItem>
                  <SelectItem value="Lifestyle">Lifestyle</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <div>
            <label htmlFor="platformFilter" className="block text-sm font-medium text-gray-700 mb-2">Platform</label>
            <div className="relative">
              <Select
                value={platformFilter}
                onValueChange={(value) => setPlatformFilter(value)}
              >
                <SelectTrigger id="platformFilter" className="w-full">
                  <SelectValue placeholder="All Platforms" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Platforms</SelectItem>
                  <SelectItem value="Instagram">Instagram</SelectItem>
                  <SelectItem value="TikTok">TikTok</SelectItem>
                  <SelectItem value="YouTube">YouTube</SelectItem>
                  <SelectItem value="Facebook">Facebook</SelectItem>
                  <SelectItem value="Twitter">Twitter</SelectItem>
                  <SelectItem value="Blog">Blog</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <div>
            <label htmlFor="followerRangeFilter" className="block text-sm font-medium text-gray-700 mb-2">Followers</label>
            <div className="relative">
              <Select
                value={followerRangeFilter}
                onValueChange={(value) => setFollowerRangeFilter(value)}
              >
                <SelectTrigger id="followerRangeFilter" className="w-full">
                  <SelectValue placeholder="All Ranges" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Ranges</SelectItem>
                  <SelectItem value="under50k">Under 50K</SelectItem>
                  <SelectItem value="50k-100k">50K - 100K</SelectItem>
                  <SelectItem value="100k-250k">100K - 250K</SelectItem>
                  <SelectItem value="250k-500k">250K - 500K</SelectItem>
                  <SelectItem value="500k-1m">500K - 1M</SelectItem>
                  <SelectItem value="over1m">Over 1M</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <div>
            <label htmlFor="ageFilter" className="block text-sm font-medium text-gray-700 mb-2">Age Range</label>
            <div className="relative">
              <Select
                value={ageFilter}
                onValueChange={(value) => setAgeFilter(value)}
              >
                <SelectTrigger id="ageFilter" className="w-full">
                  <SelectValue placeholder="All Ages" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Ages</SelectItem>
                  <SelectItem value="18-24">18-24 years</SelectItem>
                  <SelectItem value="25-34">25-34 years</SelectItem>
                  <SelectItem value="35-44">35-44 years</SelectItem>
                  <SelectItem value="45-54">45-54 years</SelectItem>
                  <SelectItem value="55+">55+ years</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <div>
            <label htmlFor="sortBy" className="block text-sm font-medium text-gray-700 mb-2">Sort By</label>
            <div className="relative">
              <Select
                value={sortBy}
                onValueChange={(value) => setSortBy(value)}
              >
                <SelectTrigger id="sortBy" className="w-full">
                  <SelectValue placeholder="Relevance" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="relevance">Relevance</SelectItem>
                  <SelectItem value="followers">Followers (High to Low)</SelectItem>
                  <SelectItem value="engagement">Engagement Rate (High to Low)</SelectItem>
                  <SelectItem value="rating">Rating (High to Low)</SelectItem>
                  <SelectItem value="campaigns">Campaign Experience (High to Low)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </div>

      {/* Influencer Cards */}
      <div className="mt-6">
        {loading ? (
          null
        ) : error ? (
          <div className="bg-red-50 border border-red-100 rounded-lg p-4 my-4">
            <div className="flex items-center">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-red-500 mr-3" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <p className="text-sm text-red-700 font-medium">{error}</p>
            </div>
          </div>
        ) : filteredInfluencers.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-component-name="Influencers">
            {filteredInfluencers.map((influencer, index) => (
              <div key={`${influencer.id}-${index}`} className="bg-white rounded-lg shadow-md border border-gray-100 overflow-hidden hover:shadow-lg transition-shadow duration-200">
                <div className="p-6">
                  <div className="flex items-center">
                    <div className="h-14 w-14 rounded-full overflow-hidden bg-gray-200 border border-gray-100 shadow-sm">
                      {(influencer.photoURL || influencer.profileImageUrl || influencer.profileImage || influencer.avatar) ? (
                        <img 
                          src={influencer.photoURL || influencer.profileImageUrl || influencer.profileImage || influencer.avatar} 
                          alt={influencer.displayName} 
                          className="h-full w-full object-cover" 
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.onerror = null;
                            e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(influencer.displayName || 'User')}&size=150&background=3B5BA5&color=fff&bold=true&format=svg`;
                          }}
                        />
                      ) : (
                        <div className="h-full w-full flex items-center justify-center bg-primary text-white text-lg font-medium" data-component-name="Influencers">
                          {influencer.displayName?.charAt(0) || '?'}
                        </div>
                      )}
                    </div>
                    <div className="ml-4">
                      <h3 className="text-lg font-medium text-gray-800">{influencer.displayName}</h3>
                      <div className="flex items-center mt-1">
                        <div className="flex items-center">
                          {[...Array(5)].map((_, i) => (
                            <svg key={i} className={`h-4 w-4 ${i < Math.floor(influencer.rating || 0) ? 'text-yellow-400' : 'text-gray-300'}`} fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 15.585l-7.071 3.535 1.357-7.9-5.714-5.573 7.9-1.143L10 0l3.528 7.143 7.9 1.143-5.714 5.572 1.357 7.9z" clipRule="evenodd" />
                            </svg>
                          ))}
                        </div>
                        <span className="ml-1 text-sm text-gray-500">({influencer.rating || '0'})</span>
                      </div>
                      <div className="mt-1 flex items-center">
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-50 text-blue-800 border border-blue-100">
                          {influencer.previousCampaigns || '0'} campaigns
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="mt-6 flex space-x-3">
                    <Link
                      to={`/brand/influencers/${influencer.id}`}
                      className="flex-1 inline-flex justify-center items-center px-4 py-2.5 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors duration-200"
                    >
                      View Profile
                    </Link>
                    <button
                      className="flex-1 inline-flex justify-center items-center px-4 py-2.5 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors duration-200"
                    >
                      Invite
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-md border border-gray-100 p-8 text-center">
            <Funnel className="mx-auto h-16 w-16 text-gray-300 mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No influencers found</h3>
            <p className="text-gray-600 mb-5 max-w-md mx-auto">
              Try adjusting your search or filter criteria to find more influencers.
            </p>
            <button
              onClick={() => {
                setSearchTerm('');
                setCategoryFilter('all');
                setPlatformFilter('all');
                setFollowerRangeFilter('all');
                setSortBy('relevance');
              }}
              className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors duration-200"
            >
              Reset Filters
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Influencers;
